import {
  View,
  Text,
  Pressable,
  ScrollView,
  ActivityIndicator,
  Modal,
  TextInput,
} from 'react-native';
import { useState } from 'react';
import { getMarylandLocalityLabel } from '@perdiem/trpc/maryland-local-tax';
import { useIsFocused, usePathname, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { trpc } from '../../lib/trpc';
import { performSignOut } from '../../lib/sign-out';
import { useUnregisterPushNotifications } from '../../hooks/usePushNotifications';
import {
  Card,
  SectionHeader,
  Button,
  LoadingSpinner,
  PageContainer,
  PageHeader,
  MarylandLocalitySelect,
  WebHead,
} from '../../components';
import { useColors } from '../../lib/colors';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { formatCurrency } from '../../lib/utils';
import {
  RISK_OPTIONS,
  FILING_OPTIONS,
  US_STATES,
  getThresholdForRiskTolerance,
  parseNonNegativeCurrencyInput,
  formatFilingStatus,
} from '../../lib/profile-fields';

const CUSTOM_RISK_VALUE = 'custom';
const RISK_THRESHOLD_EPSILON = 0.0001;

function formatThresholdPercent(threshold: number | string | null | undefined) {
  const parsed = Number(threshold);
  if (!Number.isFinite(parsed)) return null;
  const pct = parsed * 100;
  return `${Number.isInteger(pct) ? pct.toFixed(0) : pct.toFixed(1)}%`;
}

function getPresetRiskForThreshold(threshold: number | string | null | undefined) {
  const parsed = Number(threshold);
  if (!Number.isFinite(parsed)) return null;

  return RISK_OPTIONS.find(
    (opt) => Math.abs(getThresholdForRiskTolerance(opt.value) - parsed) < RISK_THRESHOLD_EPSILON,
  )?.value ?? null;
}

function getRiskToleranceDisplay(
  riskTolerance: string | null | undefined,
  threshold: number | string | null | undefined,
) {
  const thresholdLabel = formatThresholdPercent(threshold);
  const matchingPreset = getPresetRiskForThreshold(threshold);

  if (thresholdLabel && threshold && matchingPreset == null) {
    return `Custom (${thresholdLabel})`;
  }

  const label = RISK_OPTIONS.find((opt) => opt.value === riskTolerance)?.label ?? riskTolerance ?? '—';
  return thresholdLabel ? `${label} (${thresholdLabel})` : label;
}

function parseThresholdPercentInput(value: string) {
  const normalized = value.replace(/[%\s]/g, '');
  if (!normalized) return null;

  const parsed = Number(normalized);
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > 100) return null;
  return parsed / 100;
}

type EditField =
  | 'name'
  | 'birthYear'
  | 'lifeExpectancy'
  | 'state'
  | 'taxLocality'
  | 'riskTolerance'
  | 'filingStatus'
  | 'desiredEstateValue'
  | null;

function ProfileRow({
  label,
  value,
  onPress,
}: {
  label: string;
  value?: string | null;
  onPress?: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      className="flex-row items-center justify-between py-3 border-b border-gray-50 dark:border-gray-700"
    >
      <Text className="text-sm text-gray-600 dark:text-gray-400">{label}</Text>
      <View className="flex-row items-center gap-2">
        {value && <Text className="text-sm text-gray-800 dark:text-gray-200">{value}</Text>}
        {onPress && <Text className="text-gray-300 dark:text-gray-600">›</Text>}
      </View>
    </Pressable>
  );
}

export default function ProfileScreen() {
  const isDesktop = useIsDesktop();
  const isFocused = useIsFocused();
  const c = useColors();
  const utils = trpc.useUtils();
  const router = useRouter();
  const pathname = usePathname();
  const unregisterPushNotifications = useUnregisterPushNotifications();

  const handleSignOut = async () => {
    await performSignOut({
      source: 'profile_screen',
      route: pathname,
      unregisterPushNotifications,
    });
    router.replace('/(auth)/login');
  };

  const householdQ = trpc.households.get.useQuery(undefined, { enabled: isFocused });
  const profilesQ = trpc.profiles.list.useQuery(undefined, { enabled: isFocused });
  const firePlanQ = trpc.fire.getPlan.useQuery(undefined, { enabled: isFocused });
  const dependentsQ = trpc.dependents.list.useQuery(undefined, { enabled: isFocused });
  const invitesQ = trpc.invites.list.useQuery(undefined, { enabled: isFocused });

  const updateProfileMutation = trpc.profiles.update.useMutation({
    onSuccess: () => {
      utils.profiles.list.invalidate();
    },
  });
  const createPartnerMutation = trpc.profiles.create.useMutation({
    onSuccess: () => {
      utils.profiles.list.invalidate();
      utils.households.get.invalidate();
    },
  });
  const deletePartnerMutation = trpc.profiles.delete.useMutation({
    onSuccess: () => {
      utils.profiles.list.invalidate();
      utils.households.get.invalidate();
      utils.invites.list.invalidate();
    },
  });
  const setRetirementYearMutation = trpc.profiles.setRetirementYear.useMutation({
    onSuccess: () => {
      utils.profiles.list.invalidate();
      utils.fire.getPlan.invalidate();
      utils.fire.getRecalcStatus.invalidate();
    },
  });
  const createFirePlanMutation = trpc.fire.createPlan.useMutation({
    onSuccess: () => {
      utils.fire.getPlan.invalidate();
      utils.fire.getRecalcStatus.invalidate();
    },
  });
  const updateFirePlanMutation = trpc.fire.updatePlan.useMutation({
    onSuccess: () => {
      utils.fire.getPlan.invalidate();
      utils.fire.getRecalcStatus.invalidate();
    },
  });
  const createInviteMutation = trpc.invites.create.useMutation({
    onSuccess: () => {
      utils.invites.list.invalidate();
    },
  });
  const cancelInviteMutation = trpc.invites.cancel.useMutation({
    onSuccess: () => {
      utils.invites.list.invalidate();
    },
  });
  const createDependentMutation = trpc.dependents.create.useMutation({
    onSuccess: () => { utils.dependents.list.invalidate(); },
  });
  const updateDependentMutation = trpc.dependents.update.useMutation({
    onSuccess: () => { utils.dependents.list.invalidate(); },
  });
  const deleteDependentMutation = trpc.dependents.delete.useMutation({
    onSuccess: () => { utils.dependents.list.invalidate(); },
  });
  const setEducationPrefsMutation = trpc.dependents.setEducationPrefs.useMutation({
    onSuccess: () => { utils.dependents.list.invalidate(); },
  });

  const profiles = profilesQ.data ?? [];
  const currentProfile = profiles.find((profile) => profile.isSelf) ?? profiles[0];
  const partnerProfile = profiles.find((profile) => profile.type === 'spouse') ?? null;
  const hasPartner = !!partnerProfile;
  const isPrimaryUser = currentProfile?.type === 'primary';
  const isDemo = householdQ.data?.isDemo ?? false;
  const pendingInvites = (invitesQ.data ?? []).filter((i) => i.status === 'pending');
  const canInvitePartner =
    !!partnerProfile && !partnerProfile.hasAccount && pendingInvites.length === 0;

  // Profile edit state
  const [editField, setEditField] = useState<EditField>(null);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editBirthYear, setEditBirthYear] = useState('');
  const [editLifeExpectancy, setEditLifeExpectancy] = useState('');
  const [editState, setEditState] = useState('');
  const [editTaxLocality, setEditTaxLocality] = useState('');
  const [editRisk, setEditRisk] = useState('');
  const [editRiskThresholdPct, setEditRiskThresholdPct] = useState('');
  const [editFiling, setEditFiling] = useState('');
  const [editDesiredEstateValue, setEditDesiredEstateValue] = useState('');
  const [editError, setEditError] = useState<string | null>(null);

  const [retirementDrafts, setRetirementDrafts] = useState<Record<string, string | null>>({});

  // Partner / invite state
  const [showAddPartnerModal, setShowAddPartnerModal] = useState(false);
  const [partnerFirstName, setPartnerFirstName] = useState('');
  const [partnerLastName, setPartnerLastName] = useState('');
  const [partnerBirthYear, setPartnerBirthYear] = useState('');
  const [partnerLifeExpectancy, setPartnerLifeExpectancy] = useState('90');
  const [partnerError, setPartnerError] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [partnerToRemove, setPartnerToRemove] = useState<{ id: string; name: string } | null>(null);

  // Dependent (children) state
  const [depEditId, setDepEditId] = useState<string | null>(null);
  const [depEditFirstName, setDepEditFirstName] = useState('');
  const [depEditBirthYear, setDepEditBirthYear] = useState('');
  const [depEditK12Type, setDepEditK12Type] = useState('public');
  const [depEditCollegeType, setDepEditCollegeType] = useState('state');
  const [depEditError, setDepEditError] = useState<string | null>(null);
  const [addingDep, setAddingDep] = useState(false);
  const [newDepFirstName, setNewDepFirstName] = useState('');
  const [newDepBirthYear, setNewDepBirthYear] = useState('');
  const [newDepError, setNewDepError] = useState<string | null>(null);

  const resetPartnerForm = () => {
    setPartnerFirstName('');
    setPartnerLastName('');
    setPartnerBirthYear('');
    setPartnerLifeExpectancy('90');
    setPartnerError(null);
  };

  const closeAddPartnerModal = () => {
    setShowAddPartnerModal(false);
    resetPartnerForm();
  };

  const closeInviteModal = () => {
    setShowInviteModal(false);
    setInviteEmail('');
    setInviteError(null);
    setInviteSuccess(false);
  };

  const openEditField = (field: EditField) => {
    if (!currentProfile) return;
    setEditError(null);
    switch (field) {
      case 'name':
        setEditFirstName(currentProfile.firstName ?? '');
        setEditLastName(currentProfile.lastName ?? '');
        break;
      case 'birthYear':
        setEditBirthYear(currentProfile.birthYear?.toString() ?? '');
        break;
      case 'lifeExpectancy':
        setEditLifeExpectancy(currentProfile.lifeExpectancy?.toString() ?? '90');
        break;
      case 'state':
        setEditState(currentProfile.state ?? '');
        setEditTaxLocality(currentProfile.taxLocality ?? '');
        break;
      case 'taxLocality':
        setEditState(currentProfile.state ?? '');
        setEditTaxLocality(currentProfile.taxLocality ?? '');
        break;
      case 'riskTolerance':
        {
          const planThreshold = Number(firePlanQ.data?.threshold);
          const matchingPreset = getPresetRiskForThreshold(planThreshold);
          const fallbackRisk = currentProfile.riskTolerance ?? 'moderate';
          const threshold = Number.isFinite(planThreshold)
            ? planThreshold
            : getThresholdForRiskTolerance(fallbackRisk);

          setEditRisk(matchingPreset ?? CUSTOM_RISK_VALUE);
          setEditRiskThresholdPct(formatThresholdPercent(threshold)?.replace('%', '') ?? '');
        }
        break;
      case 'filingStatus':
        setEditFiling(currentProfile.filingStatus ?? 'single');
        break;
      case 'desiredEstateValue':
        setEditDesiredEstateValue(
          firePlanQ.data?.desiredEstateValue != null
            ? String(Math.round(Number(firePlanQ.data.desiredEstateValue)))
            : '0',
        );
        break;
    }
    setEditField(field);
  };

  const syncPrimaryPlanEndYear = async () => {
    if (currentProfile?.type !== 'primary') return;

    const planThreshold = Number(firePlanQ.data?.threshold);
    await createFirePlanMutation.mutateAsync({
      threshold: Number.isFinite(planThreshold)
        ? planThreshold
        : getThresholdForRiskTolerance(currentProfile.riskTolerance ?? 'moderate'),
      desiredEstateValue: Number(firePlanQ.data?.desiredEstateValue ?? 0),
    });
  };

  const saveField = async () => {
    if (!currentProfile) return;
    setEditError(null);
    try {
      const base = { id: currentProfile.id };
      switch (editField) {
        case 'name':
          await updateProfileMutation.mutateAsync({ ...base, firstName: editFirstName.trim(), lastName: editLastName.trim() });
          break;
        case 'birthYear': {
          const yr = parseInt(editBirthYear, 10);
          if (isNaN(yr) || yr < 1900 || yr > new Date().getFullYear()) {
            setEditError('Enter a valid year');
            return;
          }
          await updateProfileMutation.mutateAsync({
            ...base,
            birthYear: yr,
            ...(currentProfile.lifeExpectancy != null
              ? { lifeExpectancy: currentProfile.lifeExpectancy }
              : {}),
          });
          await syncPrimaryPlanEndYear();
          break;
        }
        case 'lifeExpectancy': {
          const years = parseInt(editLifeExpectancy, 10);
          if (isNaN(years) || years < 1 || years > 150) {
            setEditError('Enter an age between 1 and 150');
            return;
          }
          await updateProfileMutation.mutateAsync({ ...base, lifeExpectancy: years });
          await syncPrimaryPlanEndYear();
          break;
        }
        case 'state':
          if (editState === 'MD' && !editTaxLocality) {
            setEditError('Select your Maryland county or Baltimore City');
            return;
          }
          await updateProfileMutation.mutateAsync({
            ...base,
            state: editState,
            taxLocality: editState === 'MD' ? editTaxLocality : undefined,
          });
          break;
        case 'taxLocality':
          if (currentProfile.state !== 'MD') {
            setEditError('Maryland locality is only used for Maryland residents');
            return;
          }
          if (!editTaxLocality) {
            setEditError('Select your Maryland county or Baltimore City');
            return;
          }
          await updateProfileMutation.mutateAsync({
            ...base,
            taxLocality: editTaxLocality,
          });
          break;
        case 'riskTolerance':
          {
            const isCustomRisk = editRisk === CUSTOM_RISK_VALUE;
            const threshold = isCustomRisk
              ? parseThresholdPercentInput(editRiskThresholdPct)
              : getThresholdForRiskTolerance(editRisk);

            if (threshold == null) {
              setEditError('Enter a whole-number threshold between 1% and 100%');
              return;
            }

            if (!isCustomRisk) {
              await updateProfileMutation.mutateAsync({
                ...base,
                riskTolerance: editRisk as 'conservative' | 'moderate' | 'aggressive',
              });
            }

            if (firePlanQ.data?.id) {
              await updateFirePlanMutation.mutateAsync({
                id: firePlanQ.data.id,
                threshold,
              });
            } else {
              await createFirePlanMutation.mutateAsync({
                threshold,
                desiredEstateValue: Number(firePlanQ.data?.desiredEstateValue ?? 0),
              });
            }
          }
          break;
        case 'filingStatus':
          await updateProfileMutation.mutateAsync({
            ...base,
            filingStatus: editFiling as 'single' | 'married_filing_jointly',
          });
          break;
        case 'desiredEstateValue': {
          const parsed = parseNonNegativeCurrencyInput(editDesiredEstateValue);
          if (parsed == null) {
            setEditError('Enter a valid non-negative dollar amount');
            return;
          }
          if (firePlanQ.data?.id) {
            await updateFirePlanMutation.mutateAsync({
              id: firePlanQ.data.id,
              desiredEstateValue: parsed,
            });
          } else {
            await createFirePlanMutation.mutateAsync({
              threshold: getThresholdForRiskTolerance(currentProfile.riskTolerance ?? 'moderate'),
              desiredEstateValue: parsed,
            });
          }
          break;
        }
      }
      setEditField(null);
    } catch (e) {
      setEditError(e instanceof Error ? e.message : 'Failed to save');
    }
  };

  const handleRemovePartner = async () => {
    if (!partnerToRemove) return;
    try {
      await deletePartnerMutation.mutateAsync({ id: partnerToRemove.id });
      setPartnerToRemove(null);
    } catch {
      // surfaced via existing error UI
    }
  };

  const handleCreatePartner = async () => {
    setPartnerError(null);
    const firstName = partnerFirstName.trim();
    const lastName = partnerLastName.trim();
    const birthYear = Number.parseInt(partnerBirthYear, 10);
    const lifeExpectancy = Number.parseInt(partnerLifeExpectancy, 10);
    const currentYear = new Date().getFullYear();

    if (!firstName) {
      setPartnerError("Enter your partner's first name");
      return;
    }
    if (Number.isNaN(birthYear) || birthYear < 1900 || birthYear > currentYear) {
      setPartnerError('Enter a valid birth year');
      return;
    }
    if (Number.isNaN(lifeExpectancy) || lifeExpectancy < 1 || lifeExpectancy > 150) {
      setPartnerError('Enter a valid life expectancy');
      return;
    }
    try {
      await createPartnerMutation.mutateAsync({
        firstName,
        ...(lastName ? { lastName } : {}),
        birthYear,
        lifeExpectancy,
      });
      closeAddPartnerModal();
    } catch (e) {
      setPartnerError(e instanceof Error ? e.message : 'Failed to add partner');
    }
  };

  if (profilesQ.isLoading) return <LoadingSpinner />;

  return (
    <>
      <WebHead><title>Profile – Per Diem</title></WebHead>
      <ScrollView className="flex-1 bg-canvas dark:bg-canvas-dark">
        <PageContainer className={isDesktop ? '' : 'p-4'} size="narrow">
          <PageHeader title="Profile" closeTo="/(app)" />
          <View className="gap-4">
            {isDemo && (
              <Card>
                <Text className="text-sm font-semibold text-amber-900">Read-only demo account</Text>
                <Text className="mt-1 text-sm text-amber-800">
                  Profile edits and partner invitations are disabled in demos.
                </Text>
              </Card>
            )}

            {/* Profile */}
            <Card>
              <SectionHeader title="Profile" />
              {currentProfile ? (
                <>
                  <ProfileRow
                    label="Name"
                    value={[currentProfile.firstName, currentProfile.lastName].filter(Boolean).join(' ') || '—'}
                    onPress={isDemo ? undefined : () => openEditField('name')}
                  />
                  <ProfileRow
                    label="Birth Year"
                    value={currentProfile.birthYear?.toString()}
                    onPress={isDemo ? undefined : () => openEditField('birthYear')}
                  />
                  <ProfileRow
                    label="Life Expectancy"
                    value={currentProfile.lifeExpectancy?.toString() ?? '—'}
                    onPress={isDemo ? undefined : () => openEditField('lifeExpectancy')}
                  />
                  <ProfileRow
                    label="State"
                    value={currentProfile.state ?? '—'}
                    onPress={isDemo ? undefined : () => openEditField('state')}
                  />
                  {currentProfile.state === 'MD' && (
                    <ProfileRow
                      label="Maryland Locality"
                      value={getMarylandLocalityLabel(currentProfile.taxLocality) ?? '—'}
                      onPress={isDemo ? undefined : () => openEditField('taxLocality')}
                    />
                  )}
                  <ProfileRow
                    label="Risk Tolerance"
                    value={getRiskToleranceDisplay(currentProfile.riskTolerance, firePlanQ.data?.threshold)}
                    onPress={isDemo ? undefined : () => openEditField('riskTolerance')}
                  />
                  <ProfileRow
                    label="Target Final Estate Value"
                    value={formatCurrency(Number(firePlanQ.data?.desiredEstateValue ?? 0), { decimals: 0 })}
                    onPress={isDemo ? undefined : () => openEditField('desiredEstateValue')}
                  />
                  <ProfileRow
                    label="Filing Status"
                    value={hasPartner ? 'Married Filing Jointly' : formatFilingStatus(currentProfile.filingStatus)}
                    onPress={isDemo || hasPartner ? undefined : () => openEditField('filingStatus')}
                  />
                  {hasPartner && (
                    <Text className="pt-3 text-xs text-gray-500">
                      Households with a partner currently use Married Filing Jointly.
                    </Text>
                  )}
                </>
              ) : (
                <Text className="text-sm text-gray-400 py-2">No profile found</Text>
              )}
            </Card>

            {/* Retirement Settings */}
            <Card>
              <SectionHeader title="Retirement" />
              <Text className="text-xs text-gray-500 mb-3">
                Auto: we find the earliest age you can safely retire.{' '}
                Target year: we show your success rate for your chosen retirement year.
              </Text>
              {profiles.map((p) => {
                const displayName = [p.firstName, p.lastName].filter(Boolean).join(' ') || (p.type === 'primary' ? 'You' : 'Spouse');
                const draftValue = retirementDrafts[p.id];
                const isCustomDraft = draftValue !== null && draftValue !== undefined;
                const isAutoMode = !isCustomDraft && p.targetFireYear === null;
                const isTargetMode = isCustomDraft || p.targetFireYear !== null;
                const yearInputValue = isCustomDraft ? draftValue : p.targetFireYear !== null ? String(p.targetFireYear) : '';

                const handleModeChange = (mode: 'auto' | 'target') => {
                  if (mode === 'auto') {
                    setRetirementDrafts((d) => ({ ...d, [p.id]: null }));
                    setRetirementYearMutation.mutate({ personId: p.id, targetFireYear: null });
                  } else {
                    const defaultYear = p.targetFireYear ?? new Date().getFullYear() + 15;
                    setRetirementDrafts((d) => ({ ...d, [p.id]: String(defaultYear) }));
                  }
                };

                const handleYearCommit = () => {
                  const raw = retirementDrafts[p.id];
                  if (raw == null) return;
                  const yr = parseInt(raw, 10);
                  if (!isNaN(yr) && yr >= 2020 && yr <= 2100) {
                    setRetirementYearMutation.mutate({ personId: p.id, targetFireYear: yr });
                  }
                };

                return (
                  <View key={p.id} className="mb-3 last:mb-0">
                    {p.actualRetirementYear != null && <Text className="text-green-700 dark:text-green-400">Retired in {p.actualRetirementYear}. Edit arrangements on FIRE Plan.</Text>}
                    {profiles.length > 1 && (
                      <Text className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1.5">{displayName}</Text>
                    )}
                    <View className="flex-row items-center gap-3 flex-wrap">
                      <Pressable
                        onPress={isDemo || !isPrimaryUser || p.actualRetirementYear != null ? undefined : () => handleModeChange('auto')}
                        className="flex-row items-center gap-1.5"
                        disabled={isDemo || !isPrimaryUser || p.actualRetirementYear != null}
                      >
                        <View
                          className={`w-4 h-4 rounded-full border-2 items-center justify-center ${
                            isAutoMode ? 'border-green-600' : 'border-gray-300'
                          }`}
                        >
                          {isAutoMode && <View className="w-2 h-2 rounded-full bg-forest-600" />}
                        </View>
                        <Text className={`text-sm ${isAutoMode ? 'text-green-700 font-medium' : 'text-gray-500'}`}>
                          Auto
                        </Text>
                      </Pressable>

                      <Pressable
                        onPress={isDemo || !isPrimaryUser || p.actualRetirementYear != null ? undefined : () => handleModeChange('target')}
                        className="flex-row items-center gap-1.5"
                        disabled={isDemo || !isPrimaryUser || p.actualRetirementYear != null}
                      >
                        <View
                          className={`w-4 h-4 rounded-full border-2 items-center justify-center ${
                            isTargetMode ? 'border-green-600' : 'border-gray-300'
                          }`}
                        >
                          {isTargetMode && <View className="w-2 h-2 rounded-full bg-forest-600" />}
                        </View>
                        <Text className={`text-sm ${isTargetMode ? 'text-green-700 font-medium' : 'text-gray-500'}`}>
                          Target year
                        </Text>
                      </Pressable>

                      {isTargetMode && (
                        <TextInput
                          className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 text-sm text-gray-800 dark:text-gray-200 w-20 text-center"
                          value={yearInputValue}
                          onChangeText={(v) => setRetirementDrafts((d) => ({ ...d, [p.id]: v }))}
                          onBlur={handleYearCommit}
                          onSubmitEditing={handleYearCommit}
                          keyboardType="number-pad"
                          maxLength={4}
                          placeholder="Year"
                          placeholderTextColor="#9ca3af"
                          editable={!isDemo && isPrimaryUser && p.actualRetirementYear == null}
                        />
                      )}

                      {setRetirementYearMutation.isPending && (
                        <ActivityIndicator size="small" color={c.primary} />
                      )}
                    </View>
                  </View>
                );
              })}
            </Card>

            {/* Household Members */}
            <Card>
              <SectionHeader title="Household" />
              {profiles.map((p) => (
                <View key={p.id} className="flex-row items-center justify-between py-3 border-b border-gray-50 dark:border-gray-700">
                  <View>
                    <Text className="text-sm text-gray-800 dark:text-gray-200">
                      {[p.firstName, p.lastName].filter(Boolean).join(' ') || 'Unnamed'}
                      {p.isSelf ? ' (You)' : ''}
                    </Text>
                    <Text className="text-xs text-gray-400 capitalize">{p.type}</Text>
                    {p.type === 'spouse' && (
                      <Text className="text-xs text-gray-400">
                        {p.hasAccount
                          ? 'Has account access'
                          : pendingInvites.length > 0
                            ? 'Invite pending'
                            : 'No account access yet'}
                      </Text>
                    )}
                  </View>
                  {isPrimaryUser && p.type === 'spouse' && !isDemo && (
                    <Pressable
                      onPress={() => setPartnerToRemove({
                        id: p.id,
                        name: [p.firstName, p.lastName].filter(Boolean).join(' ') || 'your partner',
                      })}
                      disabled={deletePartnerMutation.isPending}
                    >
                      <Text className="text-xs text-red-500">Remove</Text>
                    </Pressable>
                  )}
                </View>
              ))}
              {pendingInvites.map((inv) => (
                <View key={inv.id} className="flex-row items-center justify-between py-3 border-b border-gray-50 dark:border-gray-700">
                  <View>
                    <Text className="text-sm text-gray-600 dark:text-gray-400">{inv.email}</Text>
                    <Text className="text-xs text-amber-500">Pending invite</Text>
                  </View>
                  <Pressable
                    onPress={() => cancelInviteMutation.mutate({ id: inv.id })}
                    disabled={isDemo || cancelInviteMutation.isPending}
                  >
                    <Text className="text-xs text-red-500">Cancel</Text>
                  </Pressable>
                </View>
              ))}
              {isDemo ? (
                <Text className="mt-3 text-xs text-gray-500">
                  Demo households are fixed snapshots, so partner invitations are disabled.
                </Text>
              ) : !hasPartner ? (
                <Button
                  size="sm"
                  className="mt-3"
                  onPress={() => {
                    resetPartnerForm();
                    setPartnerLastName(currentProfile?.lastName ?? '');
                    setShowAddPartnerModal(true);
                  }}
                >
                  Add Partner
                </Button>
              ) : canInvitePartner ? (
                <Button
                  size="sm"
                  className="mt-3"
                  onPress={() => {
                    closeInviteModal();
                    setShowInviteModal(true);
                  }}
                >
                  Invite Partner
                </Button>
              ) : pendingInvites.length > 0 ? (
                <Text className="mt-3 text-xs text-gray-500">
                  A partner invite is already pending.
                </Text>
              ) : partnerProfile?.hasAccount ? (
                <Text className="mt-3 text-xs text-gray-500">
                  Your partner already has account access.
                </Text>
              ) : null}
            </Card>

            {/* Children */}
            <Card>
              <SectionHeader title="Children" />
              {(dependentsQ.data ?? []).map((dep) => {
                const currentAge = new Date().getFullYear() - dep.birthYear;
                const prefs = dep.educationPrefs;
                const isEditing = depEditId === dep.id;
                return (
                  <View key={dep.id} className="py-3 border-b border-gray-50 dark:border-gray-700">
                    {isEditing ? (
                      <View className="gap-2">
                        <View className="flex-row gap-2">
                          <TextInput
                            value={depEditFirstName}
                            onChangeText={(v) => { setDepEditFirstName(v); setDepEditError(null); }}
                            placeholder="First name"
                            autoCapitalize="words"
                            className="flex-1 border border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-sm text-gray-800 dark:text-gray-200"
                          />
                          <TextInput
                            value={depEditBirthYear}
                            onChangeText={(v) => { setDepEditBirthYear(v); setDepEditError(null); }}
                            placeholder="Birth year"
                            keyboardType="numeric"
                            className="w-28 border border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-sm text-gray-800 dark:text-gray-200"
                          />
                        </View>
                        <Text className="text-xs text-gray-500 dark:text-gray-400 font-medium mt-1">K-12</Text>
                        <View className="flex-row gap-2">
                          {(['public', 'private'] as const).map((t) => (
                            <Button key={t} size="sm" variant={depEditK12Type === t ? 'primary' : 'secondary'} onPress={() => setDepEditK12Type(t)}>
                              {t === 'public' ? 'Public' : 'Private'}
                            </Button>
                          ))}
                        </View>
                        <Text className="text-xs text-gray-500 dark:text-gray-400 font-medium mt-1">College</Text>
                        <View className="flex-row gap-2">
                          {(['state', 'private', 'none'] as const).map((t) => (
                            <Button key={t} size="sm" variant={depEditCollegeType === t ? 'primary' : 'secondary'} onPress={() => setDepEditCollegeType(t)}>
                              {t === 'state' ? 'State' : t === 'private' ? 'Private' : 'None'}
                            </Button>
                          ))}
                        </View>
                        {depEditError && (
                          <Text className="text-xs text-red-500">{depEditError}</Text>
                        )}
                        <View className="flex-row gap-2 mt-1">
                          <Button
                            size="sm"
                            onPress={async () => {
                              const year = parseInt(depEditBirthYear);
                              if (!depEditFirstName.trim()) { setDepEditError('Name is required.'); return; }
                              if (!year || year < 1990 || year > new Date().getFullYear()) { setDepEditError('Enter a valid birth year.'); return; }
                              await updateDependentMutation.mutateAsync({ id: dep.id, firstName: depEditFirstName.trim(), birthYear: year });
                              await setEducationPrefsMutation.mutateAsync({ dependentId: dep.id, k12Type: depEditK12Type as 'public' | 'private', collegeType: depEditCollegeType as 'none' | 'state' | 'private' });
                              setDepEditId(null);
                            }}
                            loading={updateDependentMutation.isPending || setEducationPrefsMutation.isPending}
                          >
                            Save
                          </Button>
                          <Button size="sm" variant="ghost" onPress={() => setDepEditId(null)}>Cancel</Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onPress={() => deleteDependentMutation.mutate({ id: dep.id })}
                            loading={deleteDependentMutation.isPending}
                          >
                            <Text className="text-red-500 text-xs">Delete</Text>
                          </Button>
                        </View>
                      </View>
                    ) : (
                      <Pressable
                        onPress={() => {
                          setDepEditId(dep.id);
                          setDepEditFirstName(dep.firstName);
                          setDepEditBirthYear(String(dep.birthYear));
                          setDepEditK12Type(prefs?.k12Type ?? 'public' as string);
                          setDepEditCollegeType(prefs?.collegeType ?? 'state' as string);
                          setDepEditError(null);
                        }}
                        className="flex-row items-center justify-between"
                      >
                        <View>
                          <Text className="text-sm text-gray-800 dark:text-gray-200">{dep.firstName}</Text>
                          <Text className="text-xs text-gray-400">
                            Born {dep.birthYear} · age {currentAge} · {prefs?.k12Type === 'private' ? 'Private K-12' : 'Public K-12'} · {prefs?.collegeType === 'none' ? 'No college' : prefs?.collegeType === 'private' ? 'Private college' : 'State college'}
                          </Text>
                        </View>
                        <Text className="text-gray-300 dark:text-gray-600">›</Text>
                      </Pressable>
                    )}
                  </View>
                );
              })}
              {addingDep ? (
                <View className="gap-2 pt-3">
                  <View className="flex-row gap-2">
                    <TextInput
                      value={newDepFirstName}
                      onChangeText={(v) => { setNewDepFirstName(v); setNewDepError(null); }}
                      placeholder="First name"
                      autoCapitalize="words"
                      className="flex-1 border border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-sm text-gray-800 dark:text-gray-200"
                    />
                    <TextInput
                      value={newDepBirthYear}
                      onChangeText={(v) => { setNewDepBirthYear(v); setNewDepError(null); }}
                      placeholder="Birth year"
                      keyboardType="numeric"
                      className="w-28 border border-gray-200 dark:border-gray-600 rounded-xl px-3 py-2 text-sm text-gray-800 dark:text-gray-200"
                    />
                  </View>
                  {newDepError && <Text className="text-xs text-red-500">{newDepError}</Text>}
                  <View className="flex-row gap-2">
                    <Button
                      size="sm"
                      onPress={async () => {
                        const year = parseInt(newDepBirthYear);
                        if (!newDepFirstName.trim()) { setNewDepError('Name is required.'); return; }
                        if (!year || year < 1990 || year > new Date().getFullYear()) { setNewDepError('Enter a valid birth year.'); return; }
                        await createDependentMutation.mutateAsync({ firstName: newDepFirstName.trim(), birthYear: year, relationship: 'child' });
                        setNewDepFirstName('');
                        setNewDepBirthYear('');
                        setAddingDep(false);
                      }}
                      loading={createDependentMutation.isPending}
                    >
                      Add
                    </Button>
                    <Button size="sm" variant="ghost" onPress={() => { setAddingDep(false); setNewDepFirstName(''); setNewDepBirthYear(''); setNewDepError(null); }}>
                      Cancel
                    </Button>
                  </View>
                </View>
              ) : (
                <Button size="sm" className="mt-3" onPress={() => setAddingDep(true)}>
                  + Add Child
                </Button>
              )}
            </Card>

            {/* Sign out */}
            <Card>
              <Pressable
                onPress={handleSignOut}
                className="flex-row items-center gap-3 active:opacity-70"
              >
                <Ionicons name="log-out-outline" size={20} color="#dc2626" />
                <Text className="text-sm font-medium text-red-600 dark:text-red-400">
                  Sign out
                </Text>
              </Pressable>
            </Card>
          </View>
        </PageContainer>
      </ScrollView>

      {/* Profile Edit Modal */}
      <Modal visible={editField !== null} transparent animationType="fade" onRequestClose={() => setEditField(null)}>
        <Pressable className="flex-1 bg-black/50 justify-center items-center p-6" onPress={() => setEditField(null)}>
          <Pressable className="bg-white dark:bg-gray-800 rounded-2xl p-5 w-full max-w-sm" onPress={(e) => e.stopPropagation()}>
            <Text className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {editField === 'name' ? 'Edit Name' :
               editField === 'birthYear' ? 'Edit Birth Year' :
               editField === 'lifeExpectancy' ? 'Edit Life Expectancy' :
               editField === 'state' ? 'Select State' :
               editField === 'taxLocality' ? 'Select Maryland Locality' :
               editField === 'riskTolerance' ? 'Risk Tolerance' :
               editField === 'filingStatus' ? 'Filing Status' :
               editField === 'desiredEstateValue' ? 'Target Final Estate Value' : ''}
            </Text>

            {editField === 'name' && (
              <View className="gap-3">
                <TextInput
                  className="border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200"
                  placeholder="First name"
                  value={editFirstName}
                  onChangeText={setEditFirstName}
                  autoFocus
                />
                <TextInput
                  className="border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200"
                  placeholder="Last name"
                  value={editLastName}
                  onChangeText={setEditLastName}
                />
              </View>
            )}

            {editField === 'birthYear' && (
              <TextInput
                className="border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200"
                placeholder="e.g. 1990"
                value={editBirthYear}
                onChangeText={setEditBirthYear}
                keyboardType="number-pad"
                maxLength={4}
                autoFocus
              />
            )}

            {editField === 'lifeExpectancy' && (
              <View className="gap-2">
                <Text className="text-sm text-gray-500 dark:text-gray-400">
                  The age through which Per Diem plans your portfolio. Conservative planners use 95 or older.
                </Text>
                <TextInput
                  className="border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200"
                  placeholder="90"
                  value={editLifeExpectancy}
                  onChangeText={setEditLifeExpectancy}
                  keyboardType="number-pad"
                  maxLength={3}
                  autoFocus
                />
              </View>
            )}

            {editField === 'state' && (
              <View className="gap-4">
                <ScrollView className="max-h-56">
                  {US_STATES.map((s) => (
                    <Pressable
                      key={s}
                      onPress={() => {
                        setEditState(s);
                        if (s !== 'MD') setEditTaxLocality('');
                      }}
                      className={`py-2.5 px-3 rounded-lg ${editState === s ? 'bg-green-50' : ''}`}
                    >
                      <Text className={`text-sm ${editState === s ? 'text-green-700 font-semibold' : 'text-gray-700'}`}>
                        {s}
                      </Text>
                    </Pressable>
                  ))}
                </ScrollView>
                {editState === 'MD' && (
                  <View>
                    <Text className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                      Maryland County or Baltimore City
                    </Text>
                    <MarylandLocalitySelect
                      value={editTaxLocality}
                      onChange={setEditTaxLocality}
                    />
                  </View>
                )}
              </View>
            )}

            {editField === 'taxLocality' && (
              <MarylandLocalitySelect
                value={editTaxLocality}
                onChange={setEditTaxLocality}
              />
            )}

            {editField === 'riskTolerance' && (
              <View className="gap-2">
                {RISK_OPTIONS.map((opt) => (
                  <Pressable
                    key={opt.value}
                    onPress={() => setEditRisk(opt.value)}
                    className={`py-2.5 px-3 rounded-lg ${
                      editRisk === opt.value ? 'bg-green-50 dark:bg-green-900/30' : ''
                    }`}
                  >
                    <View className="flex-row items-center justify-between gap-3">
                      <Text
                        className={`text-sm ${
                          editRisk === opt.value
                            ? 'text-green-700 dark:text-green-200 font-semibold'
                            : 'text-gray-800 dark:text-gray-200'
                        }`}
                      >
                        {opt.label}
                      </Text>
                      <Text
                        className={`text-xs ${
                          editRisk === opt.value
                            ? 'text-green-700 dark:text-green-200'
                            : 'text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        {formatThresholdPercent(getThresholdForRiskTolerance(opt.value))}
                      </Text>
                    </View>
                  </Pressable>
                ))}
                <Pressable
                  onPress={() => setEditRisk(CUSTOM_RISK_VALUE)}
                  className={`py-2.5 px-3 rounded-lg ${
                    editRisk === CUSTOM_RISK_VALUE ? 'bg-green-50 dark:bg-green-900/30' : ''
                  }`}
                >
                  <Text
                    className={`text-sm ${
                      editRisk === CUSTOM_RISK_VALUE
                        ? 'text-green-700 dark:text-green-200 font-semibold'
                        : 'text-gray-800 dark:text-gray-200'
                    }`}
                  >
                    Custom
                  </Text>
                </Pressable>
                {editRisk === CUSTOM_RISK_VALUE && (
                  <View className="mt-1">
                    <Text className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                      Success threshold
                    </Text>
                    <View className="flex-row items-center rounded-lg border border-gray-200 dark:border-gray-600 px-3 py-2">
                      <TextInput
                        className="flex-1 text-sm text-gray-800 dark:text-gray-200"
                        placeholder="87"
                        placeholderTextColor={c.textTertiary}
                        value={editRiskThresholdPct}
                        onChangeText={setEditRiskThresholdPct}
                        keyboardType="number-pad"
                        maxLength={3}
                        autoFocus
                      />
                      <Text className="ml-2 text-sm text-gray-500 dark:text-gray-400">%</Text>
                    </View>
                  </View>
                )}
              </View>
            )}

            {editField === 'filingStatus' && (
              <View className="gap-1">
                {FILING_OPTIONS.map((opt) => (
                  <Pressable
                    key={opt.value}
                    onPress={() => setEditFiling(opt.value)}
                    className={`py-2.5 px-3 rounded-lg ${editFiling === opt.value ? 'bg-green-50' : ''}`}
                  >
                    <Text className={`text-sm ${editFiling === opt.value ? 'text-green-700 font-semibold' : 'text-gray-700'}`}>
                      {opt.label}
                    </Text>
                  </Pressable>
                ))}
              </View>
            )}

            {editField === 'desiredEstateValue' && (
              <View className="gap-3">
                <Text className="text-sm text-gray-500">
                  Set the amount you want left in your estate at the end of the plan. Use 0 if you do not have a legacy target.
                </Text>
                <View className="flex-row items-center border border-gray-200 rounded-lg px-3 py-2">
                  <Text className="text-sm text-gray-400 mr-1">$</Text>
                  <TextInput
                    className="flex-1 text-sm"
                    placeholder="0"
                    value={editDesiredEstateValue}
                    onChangeText={setEditDesiredEstateValue}
                    keyboardType="number-pad"
                    autoFocus
                  />
                </View>
              </View>
            )}

            {editError && (
              <Text className="text-xs text-red-600 mt-2">{editError}</Text>
            )}

            <View className="flex-row gap-2 mt-4">
              <Button
                size="sm"
                className="flex-1"
                onPress={saveField}
                loading={
                  updateProfileMutation.isPending
                  || createFirePlanMutation.isPending
                  || updateFirePlanMutation.isPending
                }
              >
                Save
              </Button>
              <Button size="sm" variant="secondary" onPress={() => setEditField(null)}>
                Cancel
              </Button>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Add Partner Modal */}
      <Modal visible={showAddPartnerModal} transparent animationType="fade" onRequestClose={closeAddPartnerModal}>
        <Pressable className="flex-1 bg-black/50 justify-center items-center p-6" onPress={closeAddPartnerModal}>
          <Pressable className="bg-white dark:bg-gray-800 rounded-2xl p-5 w-full max-w-sm" onPress={(e) => e.stopPropagation()}>
            <Text className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Add Partner</Text>
            <Text className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Create your partner&apos;s profile now. You can invite them to claim account access later.
            </Text>

            <View className="gap-3">
              <TextInput
                className="border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200"
                placeholder="First name"
                value={partnerFirstName}
                onChangeText={setPartnerFirstName}
                autoFocus
              />
              <TextInput
                className="border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200"
                placeholder="Last name (optional)"
                value={partnerLastName}
                onChangeText={setPartnerLastName}
              />
              <TextInput
                className="border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200"
                placeholder="Birth year"
                value={partnerBirthYear}
                onChangeText={setPartnerBirthYear}
                keyboardType="number-pad"
                maxLength={4}
              />
              <TextInput
                className="border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200"
                placeholder="Life expectancy"
                value={partnerLifeExpectancy}
                onChangeText={setPartnerLifeExpectancy}
                keyboardType="number-pad"
                maxLength={3}
              />
              <Text className="text-xs text-gray-500">
                We use birth year, retirement settings, and life expectancy for life events and FIRE projections.
              </Text>
              {partnerError && (
                <Text className="text-xs text-red-600">{partnerError}</Text>
              )}
              <View className="flex-row gap-2">
                <Button
                  size="sm"
                  className="flex-1"
                  onPress={handleCreatePartner}
                  loading={createPartnerMutation.isPending}
                >
                  Add Partner
                </Button>
                <Button size="sm" variant="secondary" onPress={closeAddPartnerModal}>
                  Cancel
                </Button>
              </View>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Invite Partner Modal */}
      <Modal visible={showInviteModal} transparent animationType="fade" onRequestClose={closeInviteModal}>
        <Pressable className="flex-1 bg-black/50 justify-center items-center p-6" onPress={closeInviteModal}>
          <Pressable className="bg-white dark:bg-gray-800 rounded-2xl p-5 w-full max-w-sm" onPress={(e) => e.stopPropagation()}>
            <Text className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Invite Partner</Text>
            <Text className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Send your existing partner profile an email invite so they can claim household access and collaborate with you.
            </Text>

            {inviteSuccess ? (
              <View className="gap-3">
                <View className="rounded-xl bg-green-50 px-4 py-3">
                  <Text className="text-sm text-green-700">Invite sent! They&apos;ll receive an email with a link to join.</Text>
                </View>
                <Button size="sm" variant="secondary" onPress={closeInviteModal}>
                  Done
                </Button>
              </View>
            ) : (
              <View className="gap-3">
                <TextInput
                  className="border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200"
                  placeholder="partner@email.com"
                  value={inviteEmail}
                  onChangeText={setInviteEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoFocus
                />
                {inviteError && (
                  <Text className="text-xs text-red-600">{inviteError}</Text>
                )}
                <View className="flex-row gap-2">
                  <Button
                    size="sm"
                    className="flex-1"
                    loading={createInviteMutation.isPending}
                    onPress={async () => {
                      setInviteError(null);
                      if (!partnerProfile) {
                        setInviteError('Add a partner before sending an invite');
                        return;
                      }
                      if (!inviteEmail.trim() || !inviteEmail.includes('@')) {
                        setInviteError('Enter a valid email address');
                        return;
                      }
                      try {
                        await createInviteMutation.mutateAsync({
                          email: inviteEmail.trim(),
                          personId: partnerProfile.id,
                        });
                        setInviteSuccess(true);
                      } catch (e) {
                        setInviteError(e instanceof Error ? e.message : 'Failed to send invite');
                      }
                    }}
                  >
                    Send Invite
                  </Button>
                  <Button size="sm" variant="secondary" onPress={closeInviteModal}>
                    Cancel
                  </Button>
                </View>
              </View>
            )}
          </Pressable>
        </Pressable>
      </Modal>

      {/* Remove Partner Modal */}
      <Modal visible={partnerToRemove !== null} transparent animationType="fade" onRequestClose={() => setPartnerToRemove(null)}>
        <Pressable className="flex-1 bg-black/50 justify-center items-center p-6" onPress={() => setPartnerToRemove(null)}>
          <Pressable className="bg-white dark:bg-gray-800 rounded-2xl p-5 w-full max-w-sm" onPress={(e) => e.stopPropagation()}>
            <Text className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Remove Partner</Text>
            <Text className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Remove {partnerToRemove?.name ?? 'this partner'} from the household? They will lose shared access to accounts, budgets, and the FIRE plan.
            </Text>
            <View className="flex-row gap-2">
              <Button
                size="sm"
                variant="destructive"
                className="flex-1"
                onPress={handleRemovePartner}
                loading={deletePartnerMutation.isPending}
              >
                Remove
              </Button>
              <Button size="sm" variant="secondary" onPress={() => setPartnerToRemove(null)}>
                Cancel
              </Button>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}
